import { create } from 'zustand';

import attemptService, { Attempt, SaveAnswerData, SubmitResult } from '../services/attempt.service';

interface Answer {
    questionId: string;
    answer: any;
    timeSpent: number;
    bookmarked: boolean;
    visited: boolean;
}

interface AttemptState {
    currentAttempt: Attempt | null;
    /** Grading summary returned by the last submit. */
    lastResult: SubmitResult['result'] | null;
    answers: Map<string, Answer>;
    loading: boolean;
    error: string | null;
    autoSaveTimer: number | null;

    // Actions
    startAttempt: (examId: string, accessCode?: string) => Promise<Attempt>;
    resumeAttempt: (attemptId: string) => Promise<void>;
    saveAnswer: (data: SaveAnswerData) => Promise<void>;
    submitAttempt: () => Promise<SubmitResult>;
    getAnswer: (questionId: string) => Answer | undefined;
    toggleBookmark: (questionId: string) => void;
    markVisited: (questionId: string) => void;
    clearAttempt: () => void;
    clearError: () => void;
}

export const useAttemptStore = create<AttemptState>((set, get) => ({
    currentAttempt: null,
    lastResult: null,
    answers: new Map(),
    loading: false,
    error: null,
    autoSaveTimer: null,

    startAttempt: async (examId, accessCode) => {
        set({ loading: true, error: null });
        try {
            const attempt = await attemptService.startAttempt({ examId, accessCode });
            set({ currentAttempt: attempt, answers: new Map(), loading: false });
            return attempt;
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    resumeAttempt: async (attemptId) => {
        set({ loading: true, error: null });
        try {
            // Backend responds { attempt, answers }
            const { attempt, answers } = await attemptService.resumeAttempt(attemptId);

            // Restore saved answers (questionId may be populated)
            const answersMap = new Map<string, Answer>();
            (answers || []).forEach((ans: any) => {
                const questionId = String(ans.questionId?._id || ans.questionId);
                if (ans.answer === undefined || ans.answer === null) return;
                answersMap.set(questionId, {
                    questionId,
                    answer: ans.answer,
                    timeSpent: ans.timeSpentSeconds || 0,
                    bookmarked: ans.bookmarked || false,
                    visited: true,
                });
            });

            set({ currentAttempt: attempt, answers: answersMap, loading: false });
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    saveAnswer: async (data) => {
        const { currentAttempt, answers } = get();
        if (!currentAttempt) {
            throw new Error('No active attempt');
        }

        // Update local state first for immediate feedback
        const newAnswers = new Map(answers);
        const existing = newAnswers.get(data.questionId) || {
            questionId: data.questionId,
            timeSpent: 0,
            bookmarked: false,
            visited: true,
        };

        newAnswers.set(data.questionId, {
            ...existing,
            answer: data.answer,
            timeSpent: (existing.timeSpent || 0) + (data.timeSpent || 0),
            bookmarked: data.bookmarked ?? existing.bookmarked,
        });

        set({ answers: newAnswers });

        // Save to backend
        try {
            await attemptService.saveAnswer(currentAttempt._id, data);
        } catch (error: any) {
            console.error('Failed to save answer:', error);
            // Don't throw - auto-save will retry
        }
    },

    submitAttempt: async () => {
        const { currentAttempt } = get();
        if (!currentAttempt) {
            throw new Error('No active attempt');
        }

        set({ loading: true, error: null });
        try {
            // Backend responds { attempt, result }
            const submitResult = await attemptService.submitAttempt(currentAttempt._id);
            set({ currentAttempt: submitResult.attempt, lastResult: submitResult.result, loading: false });
            return submitResult;
        } catch (error: any) {
            set({ error: error.message, loading: false });
            throw error;
        }
    },

    getAnswer: (questionId) => {
        return get().answers.get(questionId);
    },

    toggleBookmark: (questionId) => {
        const { answers } = get();
        const newAnswers = new Map(answers);
        const existing = newAnswers.get(questionId) || {
            questionId,
            answer: undefined,
            timeSpent: 0,
            bookmarked: false,
            visited: true,
        };

        newAnswers.set(questionId, {
            ...existing,
            bookmarked: !existing.bookmarked,
        });

        set({ answers: newAnswers });
    },

    markVisited: (questionId) => {
        const { answers } = get();
        if (!answers.has(questionId)) {
            const newAnswers = new Map(answers);
            newAnswers.set(questionId, {
                questionId,
                answer: undefined,
                timeSpent: 0,
                bookmarked: false,
                visited: true,
            });
            set({ answers: newAnswers });
        }
    },

    clearAttempt: () => {
        set({
            currentAttempt: null,
            lastResult: null,
            answers: new Map(),
            error: null,
            autoSaveTimer: null,
        });
    },

    clearError: () => {
        set({ error: null });
    },
}));
